import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import axios from 'axios';
import { omitBy } from 'lodash';
import {
  AlarmViewType,
  AtaPortContactIdOverrideType,
  CreateAtaPortContactIdOverrideDto,
} from '../../types/contact-id-message/contact-id-message.type';
import { additionalQueryParams } from '../../utils/utilities';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const getATAContactIDMessages = createAsyncThunk<
  PaginationType<AlarmViewType | unknown>,
  { sn: string; limit: number; page: number; order: string },
  { state: RootReducerState }
>('contactIDMessagesReducer/getATAContactIDMessages', async ({ sn, page, limit, order }, thunkAPI) => {
  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs
    );
    const mergedParams = { serial_number: sn, page, limit, order, ...additionalParams };

    const response = await axios.get<PaginationType<AlarmViewType | unknown>>(
      `${process.env.REACT_APP_API_URL}/contact-id/alarms-view`,
      { params: mergedParams }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching contact ID messages!' });
  }
});

export const getContactIDMessages = createAsyncThunk<
  PaginationType<AlarmViewType>,
  RouteParamsType & { controller: AbortController },
  { state: RootReducerState }
>('contactIDMessagesReducer/getContactIDMessages/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs
    );
    const mergedParams = omitBy({ ...params, ...additionalParams }, value => value === '');

    const response = await axios.get<PaginationType<AlarmViewType>>(
      `${process.env.REACT_APP_API_URL}/contact-id/alarms-view`,
      controller ? { signal: controller.signal, params: mergedParams } : { params: mergedParams }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching contact ID messages!' });
  }
});

export const patchContactIDOverrides = createAsyncThunk<
  { success: boolean },
  { data: CreateAtaPortContactIdOverrideDto },
  { state: RootReducerState }
>('contactIDMessagesReducer/patchContactIDOverrides', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.patch<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/contact-id/overrides`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating alarm signal overrides!' });
  }
});

export const getContactIDOverrides = createAsyncThunk<
  AtaPortContactIdOverrideType[],
  { ataId: number },
  {
    state: RootReducerState;
  }
>('contactIDMessagesReducer/getContactIDOverrides', async ({ ataId }, thunkAPI) => {
  try {
    const response = await axios.get<AtaPortContactIdOverrideType[]>(
      `${process.env.REACT_APP_API_URL}/contact-id/overrides?ataId=${ataId}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating alarm signal overrides!' });
  }
});

export const getAlarmSignalLogs = createAsyncThunk<
  {
    events?: Array<{ ts: string; message: string }>;
    sipLadder?: Array<{ attempt: number; result: string; message: string }>;
  },
  { alarmId: string },
  { state: RootReducerState; rejectValue: string }
>('contacts/getAlarmSignalLogs', async ({ alarmId }, thunkAPI) => {
  try {
    const response = await axios.get<{
      events?: Array<{ ts: string; message: string }>;
      sipLadder?: Array<{ attempt: number; result: string; message: string }>;
    }>(`${process.env.REACT_APP_API_URL}/contact-id/${alarmId}/logs`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching alarm signal logs!' });
  }
});
